import React, { useEffect, useState, useContext } from 'react';
import Modal from 'react-bootstrap/Modal';
import axios from 'axios';
import { AuthContext } from '../../../component/context/AuthContext';
import * as CONST from '../../../component/config/Constant';
import { toast } from 'react-toastify';
import { IconCircleCheck, IconCircleX, IconUser, IconClipboardCheck, IconChevronLeft, IconChevronRight, IconList } from '@tabler/icons-react';
 
export default function ViewSubmissionModal({ show, handleClose, submission }) {
    const [authUserData] = useContext(AuthContext);
    const token = authUserData && authUserData[0] ? authUserData[0].token : '';
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const questionsPerPage = 5;
 
    useEffect(() => {
        if (show && submission) {
            setCurrentPage(1);
            fetchDetails();
        }
    }, [show, submission]);
 
    async function fetchDetails() {
        if (!submission?.user_id || !submission?.question_set_id) return;
        setLoading(true);
        try {
            const url = `${CONST.API_LINK}quiz/history/${submission.user_id}/${submission.question_set_id}`;
            const resp = await axios.get(url, {
                headers: { Authorization: `Basic ${token}` },
                params: { is_guest: submission.is_guest ? 1 : 0 },
            });
 
            if (resp.data?.status === "success") {
                setDetails(resp.data.data || []);
            } else {
                setDetails([]);
                toast.error(resp.data?.message || "Failed to load details");
            }
        } catch (err) {
            setDetails([]);
            toast.error("Error fetching details");
        } finally {
            setLoading(false);
        }
    }
 
    const totalQuestions = details.length;
    const correctAnswers = details.filter(q => q.is_correct).length;
    const totalPages = Math.ceil(details.length / questionsPerPage);
    const paginatedDetails = details.slice((currentPage - 1) * questionsPerPage, currentPage * questionsPerPage);
 
    return (
        <Modal show={show} onHide={handleClose} size="lg" centered scrollable>
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="fw-bold">
                    Submission Details
                    <div className="small text-muted fw-normal">Result for {submission?.assignment_name}</div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4 bg-light-subtle">
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status"></div>
                        <div className="mt-3 text-muted">Loading quiz history...</div>
                    </div>
                ) : (
                    <>
                        {/* Summary Header */}
                        <div className="row g-3 mb-4 justify-content-center align-items-center">
                            <div className="col-md-5">
                                <div className="card shadow-none border-0 bg-primary text-white p-3 rounded-4 h-100 d-flex flex-row align-items-center gap-3">
                                    <div className="avatar avatar-md bg-white-lt rounded-3 text-white mr-2">
                                        <IconUser size={24} />
                                    </div>
                                    <div className='flex-grow-1'>
                                        <div className="fw-bold fs-3 text-white">{submission?.first_name} {submission?.last_name}</div>
                                        <div className="text-white small">{submission?.email}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-5">
                                <div className="card shadow-none border-0 bg-primary text-white p-3 rounded-4 h-100 d-flex flex-row align-items-center gap-3">
                                    <div className="avatar avatar-md bg-white-lt rounded-3 text-white mr-2">
                                        <IconClipboardCheck size={24} />
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="small opacity-75">Final Score</div>
                                        <div className="fw-bold fs-2">{correctAnswers} / {totalQuestions}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
 
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                <IconList size={20} className="text-primary" />
                                Questions Breakdown
                            </h5>
                            <span className="badge bg-white text-muted border shadow-sm px-2 py-1">Total {totalQuestions}</span>
                        </div>
 
                        {details.length > 0 ? (
                            paginatedDetails.map((q, index) => {
                                const isCorrect = q.is_correct;
                                const actualIndex = (currentPage - 1) * questionsPerPage + index + 1;
                                return (
                                    <div key={index} className="card mb-3 border-0 shadow-sm rounded-4 overflow-hidden">
                                        <div className={`card-header py-2 px-4 shadow-none border-0 ${isCorrect ? 'bg-success-lt text-success' : 'bg-danger-lt text-danger'}`}>
                                            <div className="d-flex align-items-center gap-2 w-100">
                                                {isCorrect ? <IconCircleCheck size={18} /> : <IconCircleX size={18} />}
                                                <span className="fw-bold small ml-2">{isCorrect ? 'Correct' : 'Incorrect'}</span>
                                            </div>
                                        </div>
                                        <div className="card-body p-4 bg-white">
                                            {/* FIX 1: Question number avatar - proper circle with centered number */}
                                            <div className="d-flex align-items-center gap-3 mb-4">
                                                <div
                                                    className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 text-white fw-bold ${isCorrect ? 'bg-primary' : 'bg-primary'}`}
                                                    style={{ width: '32px', height: '32px', minWidth: '32px', fontSize: '13px', lineHeight: 1 }}
                                                >
                                                    {actualIndex}
                                                </div>
                                                <h5 className="mb-0 fw-bold text-dark ml-2">{q.question_text}</h5>
                                            </div>
 
                                            {/* FIX 2: Options - no double border, always show correct in green, wrong selection in red, all others neutral */}
                                            <div className="ms-md-5 mb-3">
                                                <div className="row g-2">
                                                    {(q.options || []).map((opt, oi) => {
                                                        const isUserSelection = q.your_answer && (
                                                            (q.your_answer.id && q.your_answer.id == opt.id) ||
                                                            (q.your_answer.option_text === opt.option_text)
                                                        );
                                                        const isCorrectOption = (q.correct_answer && (
                                                            (q.correct_answer.id && q.correct_answer.id == opt.id) ||
                                                            (q.correct_answer.option_text === opt.option_text)
                                                        )) || (String(opt.is_correct) === "1" || opt.is_correct === true);
 
                                                        // Priority: correct answer always green, wrong user selection red, rest neutral
                                                        let bgStyle = { backgroundColor: '#fff', borderColor: '#dee2e6' };
                                                        let textClass = 'text-dark';
                                                        let icon = <span className="d-inline-block rounded-circle border border-secondary" style={{ width: 14, height: 14, opacity: 0.4, flexShrink: 0 }}></span>;
 
                                                        if (isCorrectOption) {
                                                            bgStyle = { backgroundColor: '#f0fdf4', borderColor: '#22c55e', borderWidth: '2px' };
                                                            textClass = 'text-success fw-bold';
                                                            icon = <IconCircleCheck size={16} className="text-success flex-shrink-0" />;
                                                        } else if (isUserSelection && !isCorrect) {
                                                            bgStyle = { backgroundColor: '#fff5f5', borderColor: '#ef4444', borderWidth: '2px' };
                                                            textClass = 'text-danger fw-bold';
                                                            icon = <IconCircleX size={16} className="text-danger flex-shrink-0" />;
                                                        }
 
                                                        return (
                                                            <div key={opt.id ?? oi} className="col-md-6">
                                                                <div
                                                                    className={`p-2 px-3 border rounded-pill d-flex align-items-center gap-2 ${textClass}`}
                                                                    style={{ fontSize: '13px', cursor: 'default', transition: 'background 0.2s', ...bgStyle }}
                                                                >
                                                                    {icon}
                                                                    <span className="text-truncate ml-2">{opt.option_text}</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
 
                                            {/* FIX 3: Your Answer - light blue bg, hover effect, result icon restored */}
                                            <div
                                                className="ms-md-5 p-3 rounded-3 border"
                                                style={{
                                                    backgroundColor: '#eff6ff',
                                                    borderColor: '#bfdbfe',
                                                    transition: 'background 0.2s, box-shadow 0.2s',
                                                    cursor: 'default',
                                                }}
                                                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#dbeafe'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(17,103,232,0.10)'; }}
                                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#eff6ff'; e.currentTarget.style.boxShadow = 'none'; }}
                                            >
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div>
                                                        <div className="small text-muted mb-1 fw-medium">Given Answer</div>
                                                        <div className={`fw-bold fs-5 ${q.your_answer ? (isCorrect ? 'text-success' : 'text-danger') : 'text-muted'}`}>
                                                            {q.your_answer ? q.your_answer.option_text : 'No response'}
                                                        </div>
                                                    </div>
                                                    {/* <div className={`d-flex align-items-center justify-content-center rounded-3 ${isCorrect ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'}`} style={{ width: 40, height: 40 }}>
                                                        {isCorrect ? <IconCircleCheck size={22} /> : <IconCircleX size={22} />}
                                                    </div> */}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-5 bg-white rounded-3 border">
                                <p className="text-muted mb-0">No history details available for this attempt.</p>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-light d-flex justify-content-between">
                {!loading && totalPages > 1 ? (
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted small me-2">Page {currentPage} of {totalPages}</span>
                        <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>
                                    <IconChevronLeft size={16} />
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>
                                    <IconChevronRight size={16} />
                                </button>
                            </li>
                        </ul>
                    </div>
                ) : <div></div>}
                <button className="btn btn-outline-secondary px-4 rounded-pill" onClick={handleClose}>Close</button>
            </Modal.Footer>
        </Modal>
    );
}